import { useEffect, useRef, useState } from "react";
import { api, API, TOKEN_KEY } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Mic, Square, Trash2, Sparkles, CheckSquare, Loader2 } from "lucide-react";

export default function Voice() {
  const [recordings, setRecordings] = useState([]);
  const [recording, setRecording] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  const load = () => api.get("/recordings").then((r) => setRecordings(r.data));
  useEffect(() => { load(); return () => clearInterval(timerRef.current); }, []);

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => chunksRef.current.push(e.data);
      mr.onstop = () => { stream.getTracks().forEach((t) => t.stop()); upload(); };
      mr.start();
      mediaRef.current = mr;
      setRecording(true);
      setSeconds(0);
      timerRef.current = setInterval(() => setSeconds((s) => s + 1), 1000);
    } catch {
      toast.error("Microphone access denied");
    }
  };

  const stop = () => {
    clearInterval(timerRef.current);
    mediaRef.current?.stop();
    setRecording(false);
  };

  const upload = async () => {
    setProcessing(true);
    const blob = new Blob(chunksRef.current, { type: "audio/webm" });
    const fd = new FormData();
    fd.append("file", blob, "recording.webm");
    fd.append("title", `Voice note ${new Date().toLocaleString()}`);
    try {
      await api.post("/recordings", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Transcribed & analyzed");
      load();
    } catch {
      toast.error("Upload/transcription failed");
    } finally {
      setProcessing(false);
    }
  };

  const remove = async (id) => { await api.delete(`/recordings/${id}`); load(); };

  const addTask = async (title) => {
    await api.post("/tasks", { title, priority: "medium", source: "voice" });
    toast.success("Task created");
  };

  const fmt = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

  return (
    <div className="space-y-6" data-testid="voice-page">
      <div>
        <div className="label-mono text-slate-500">Capture</div>
        <h1 className="text-4xl font-black tracking-tighter">Voice</h1>
      </div>

      <Card className="rounded-sm bg-slate-950 text-white border-0 shadow-none p-8 flex flex-col items-center">
        <button
          onClick={recording ? stop : start}
          disabled={processing}
          data-testid="record-button"
          className={`w-24 h-24 rounded-full flex items-center justify-center transition-all ${
            recording ? "bg-destructive ai-pulse" : "bg-ai hover:bg-blue-700"
          }`}
        >
          {processing ? <Loader2 size={32} className="animate-spin" /> : recording ? <Square size={30} /> : <Mic size={34} />}
        </button>
        <div className="mt-5 label-mono text-white/70">
          {processing ? "Transcribing & extracting action items…" : recording ? `Recording  ${fmt(seconds)}` : "Tap to capture a voice note"}
        </div>
      </Card>

      <div className="space-y-3">
        {recordings.length === 0 && <p className="text-sm text-muted-foreground">No recordings yet.</p>}
        {recordings.map((r) => (
          <Card key={r.id} className="rounded-sm border-border shadow-none p-5 group" data-testid={`recording-${r.id}`}>
            <div className="flex items-start justify-between">
              <div className="font-semibold">{r.title}</div>
              <button onClick={() => remove(r.id)} className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-destructive"><Trash2 size={16} /></button>
            </div>
            {r.storage_path && (
              <audio
                controls
                preload="none"
                className="w-full mt-3 h-9"
                data-testid={`audio-${r.id}`}
                src={`${API}/recordings/${r.id}/audio?auth=${localStorage.getItem(TOKEN_KEY)}`}
              />
            )}
            {r.summary && (
              <div className="mt-3 flex gap-2 items-start bg-ai/5 border border-ai/20 rounded-sm p-3">
                <Sparkles size={15} className="text-ai mt-0.5" />
                <p className="text-sm text-slate-700">{r.summary}</p>
              </div>
            )}
            {r.transcript && <p className="text-sm text-slate-600 mt-3 leading-relaxed">{r.transcript}</p>}
            {!r.transcript && <p className="text-sm text-muted-foreground mt-3 italic">No transcript captured.</p>}
            {r.action_items?.length > 0 && (
              <div className="mt-4">
                <div className="label-mono text-slate-500 mb-2">Action Items</div>
                <div className="space-y-1.5">
                  {r.action_items.map((a, i) => (
                    <div key={i} className="flex items-center justify-between gap-2 border-b border-border last:border-0 py-1.5">
                      <span className="text-sm">{a}</span>
                      <button onClick={() => addTask(a)} className="label-mono text-ai flex items-center gap-1 hover:underline" data-testid={`add-action-task-${r.id}-${i}`}>
                        <CheckSquare size={13} /> Add task
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </Card>
        ))}
      </div>
    </div>
  );
}
